import { ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from 'react-router-dom';
import { LoginScreen } from '@/screens/auth/LoginScreen';
import { VerifyScreen } from '@/screens/auth/VerifyScreen';
import { MainLayout } from '@/layouts/MainLayout';
import { OverviewScreen } from '@/screens/overview/OverviewScreen';
import { CustomerServiceScreen } from '@/screens/customer-service/CustomerServiceScreen';
import { EscalationsScreen } from '@/screens/escalations/EscalationsScreen';
import { FeedbackScreen } from '@/screens/feedback/FeedbackScreen';
import { RetentionScreen } from '@/screens/retention/RetentionScreen';
import { AuditLogScreen } from '@/screens/audit-log/AuditLogScreen';
import { PipelineScreen } from '@/screens/pipeline/PipelineScreen';
import { DiscordProspectsScreen } from '@/screens/discord-prospects/DiscordProspectsScreen';
import { BotPartnersScreen } from '@/screens/bot-partners/BotPartnersScreen';
import { InfrastructureScreen } from '@/screens/infrastructure/InfrastructureScreen';
import { InferenceAdminScreen } from '@/screens/inference/InferenceAdminScreen';
import { CryptoAssetsScreen } from '@/screens/crypto-assets/CryptoAssetsScreen';
import { CapTableScreen } from '@/screens/cap-table/CapTableScreen';
import { IntegrationsScreen } from '@/screens/integrations/IntegrationsScreen';
import { ServicesScreen } from '@/screens/services/ServicesScreen';
import { ServerBillingScreen } from '@/screens/server-billing/ServerBillingScreen';
import { ExchangeRoutersScreen } from '@/screens/exchange-routers/ExchangeRoutersScreen';
import { NetworksScreen } from '@/screens/networks/NetworksScreen';
import { CodeSigningScreen } from '@/screens/code-signing/CodeSigningScreen';
import { K8sHealthScreen } from '@/screens/k8s-health/K8sHealthScreen';
import { BoardScreen } from '@/screens/board/BoardScreen';
import { MeetingDetailScreen } from '@/screens/board/MeetingDetailScreen';
import { CharityVerifyScreen } from '@/screens/charity-verify/CharityVerifyScreen';
import { SigningApprovalsScreen } from '@/screens/signing-approvals/SigningApprovalsScreen';
import { UsersScreen } from '@/screens/users/UsersScreen';
import { CreatorDetailScreen } from '@/screens/users/CreatorDetailScreen';
import { EulaScreen } from '@/screens/eula/EulaScreen';
import { SystemMapScreen } from '@/screens/system-map/SystemMapScreen';
import { RewardCapsScreen } from '@/screens/reward-caps/RewardCapsScreen';
import { ComponentsScreen } from '@/screens/components/ComponentsScreen';
import { useAuthStore } from '@/store/authStore';

function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuthStore(s => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const token = useAuthStore(s => s.token);
  if (token) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Every admin route. Screens already ported into admin.nurev.io render
 * their real component; the rest render a MigrationStub that deep-links
 * to the still-working version on app.nurev.io. Update this file as
 * views get ported in follow-up sessions.
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthed>
        <LoginScreen />
      </RedirectIfAuthed>
    )
  },
  {
    path: '/auth/verify',
    element: <VerifyScreen />
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <MainLayout />
      </RequireAuth>
    ),
    children: [
      // ─── shipped here ─────────────────────────────────────────────
      { index: true, element: <OverviewScreen /> },
      { path: 'system-map', element: <SystemMapScreen /> },
      { path: 'reward-caps', element: <RewardCapsScreen /> },

      // ─── migration pending ────────────────────────────────────────
      // Each route renders a friendly stub with a deep link back to
      // app.nurev.io until the JSX view is ported.
      { path: 'customer-service', element: <CustomerServiceScreen /> },
      { path: 'escalations', element: <EscalationsScreen /> },
      { path: 'feedback', element: <FeedbackScreen /> },
      { path: 'retention', element: <RetentionScreen /> },
      { path: 'audit-log', element: <AuditLogScreen /> },
      { path: 'pipeline', element: <PipelineScreen /> },
      { path: 'discord-prospects', element: <DiscordProspectsScreen /> },
      { path: 'bot-partners', element: <BotPartnersScreen /> },
      { path: 'crypto-assets', element: <CryptoAssetsScreen /> },
      { path: 'cap-table', element: <CapTableScreen /> },
      { path: 'inference', element: <InferenceAdminScreen /> },
      { path: 'infrastructure', element: <InfrastructureScreen /> },
      { path: 'components', element: <ComponentsScreen /> },
      { path: 'integrations', element: <IntegrationsScreen /> },
      // Global EULA editor — admin scope of the dashboard's views/eula,
      // ported 2026-06-09. (Publishers still edit their per-app EULA on
      // app.nurev.io; only the platform-default moved here.)
      { path: 'eula', element: <EulaScreen /> },

      // ─── Migration-pending — deep-link to app.nurev.io ────────────
      // These views haven't been ported to native admin.nurev.io
      // screens yet (cleanup task #141 phase 2). Each renders a
      // MigrationStub that opens the working version on app.nurev.io
      // in the same tab. The publisher dashboard still hosts those
      // views; the RoleBasedLandingRedirect there is a no-op for
      // deep URLs, so admins land directly on the legacy screen.
      //
      // When a port lands, replace MigrationStub with the real
      // screen component and (in the publisher dashboard) flip the
      // matching route to AdminMovedRedirect + delete the local
      // view dir. Same playbook as the 9 already-ported screens.
      { path: 'board', element: <BoardScreen /> },
      { path: 'board/:meetingId', element: <MeetingDetailScreen /> },
      { path: 'charity-verify', element: <CharityVerifyScreen /> },
      { path: 'signing-approvals', element: <SigningApprovalsScreen /> },
      { path: 'server-billing', element: <ServerBillingScreen /> },
      { path: 'services', element: <ServicesScreen /> },
      { path: 'exchange-routers', element: <ExchangeRoutersScreen /> },
      { path: 'networks', element: <NetworksScreen /> },
      { path: 'code-signing', element: <CodeSigningScreen /> },
      { path: 'k8s-health', element: <K8sHealthScreen /> },
      // User management — three role lists + a shared detail screen.
      // Ported from the publisher dashboard's views/user/{list,info}
      // (audit 2026-06-09). One UsersScreen, parameterized by role;
      // one CreatorDetailScreen serving all three detail routes.
      {
        path: 'publishers',
        element: (
          <UsersScreen
            role="publisher"
            title="Publishers"
            description="All publisher accounts on NUREV — creators distributing apps."
            detailBase="/publishers"
          />
        )
      },
      { path: 'publishers/:id', element: <CreatorDetailScreen /> },
      {
        path: 'admin-users',
        element: (
          <UsersScreen
            role="admin"
            title="Admin Users"
            description="NUREV staff accounts with admin or super-admin roles."
            detailBase="/admin-users"
          />
        )
      },
      { path: 'admin-users/:id', element: <CreatorDetailScreen /> },
      {
        path: 'super-creators',
        element: (
          <UsersScreen
            role="super_publisher"
            title="Super Creators"
            description="Super-publisher (wholesale reseller) accounts."
            detailBase="/super-creators"
          />
        )
      },
      { path: 'super-creators/:id', element: <CreatorDetailScreen /> }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
